import React, { useCallback, useState } from 'react';

import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import Text from '~/components/core/TextComponents/Text';
import { PencilIcon } from '~/components/icons';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

const MAX_TITLE_LENGTH = 70;

export interface ChatTitleProps {
  title?: string;
  className?: string;
  chatId?: string | number | null;
  onRename?: (newTitle: string) => Promise<void>;
}

export const ChatTitle: React.FC<ChatTitleProps> = ({ title, className, chatId, onRename }) => {
  const displayTitle = title || 'New Chat';
  const canEdit = !!chatId && !!onRename;
  const { trackChatEvent } = useChatMixpanel();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(displayTitle);
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEditing = useCallback(() => {
    if (!canEdit || isEditing) return;

    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RENAME_STARTED, { renamed_chat_id: chatId });
    setEditValue(displayTitle);
    setIsEditing(true);
  }, [canEdit, isEditing, displayTitle, trackChatEvent, chatId]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue(displayTitle);
  }, [displayTitle]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const trimmed = editValue.trim();
    if (!trimmed || trimmed.length > MAX_TITLE_LENGTH || !onRename) {
      handleCancel();
      return;
    }

    if (trimmed === title) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await onRename(trimmed);

      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RENAME_SAVED, {
        renamed_chat_id: chatId,
        new_title_length: trimmed.length,
      });
      setIsEditing(false);
    } catch {
      handleCancel();
    } finally {
      setIsSaving(false);
    }
  }, [editValue, title, onRename, handleCancel, isSaving, trackChatEvent, chatId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RENAME_CANCELLED);
        handleCancel();
      }
    },
    [handleSave, handleCancel, trackChatEvent]
  );

  return (
    <div
      className={cn(
        'group flex h-[64px] cursor-pointer items-center gap-8 p-20',
        { 'cursor-default': !canEdit, 'opacity-50': isSaving, 'bg-azure-300/10': isEditing },
        className
      )}
      onClick={handleStartEditing}
    >
      {isEditing ? (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          onFocus={(e) => e.target.select()}
          disabled={isSaving}
          maxLength={MAX_TITLE_LENGTH}
          autoFocus
          className="w-full border-0 bg-transparent text-lg font-medium text-slate-900 outline-none"
        />
      ) : (
        <>
          <Text className="truncate text-slate-900">{displayTitle}</Text>
          {canEdit && (
            <PencilIcon className="shrink-0 text-slate-900 opacity-0 transition-opacity group-hover:opacity-100" />
          )}
        </>
      )}
    </div>
  );
};
